import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import CustomInput from "components/CustomInput";
import ButtonSuccess from "components/Buttons/ButtonSuccess";
import { useLazyQuery } from "@apollo/client";
import { FORGOT_PASSWORD } from "@ugg/shared/api/graphql/queries";
import { isValidEmail } from "lib/form-helpers";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import { ReactComponent as ChevronLeft } from "@ugg/shared/assets/svg/chevron-left.svg";
import { forwardMode } from "lib/monoapp";

const INPUT_CLASS =
  "[&_input]:h-[36px] [&_input]:w-full [&_input]:text-[12px] " +
  "[&_input]:text-white [&_input]:rounded-[3px] [&_input]:border-0 [&_input]:bg-purple-100 [&_input]:pl-3 " +
  "[&_input:focus]:outline-none [&_input:focus]:border-0 [&_input]:placeholder:accent-gray-200 " +
  "[&_input]:placeholder:font-normal [&_.input-label]:text-[12px] [&_.input-label]:mb-[8px] " +
  "[&_.input-label]:text-lavender-50 [&_.input-label]:font-inter";

export default function RecoverAccountForm(props) {
  const { setEmailConfirmation } = props;
  const [email, setEmail] = useState({ value: "", error: "" });
  const [resetEmailError, setResetEmailError] = useState("");
  const { search } = useLocation();

  const [sendPasswordResetEmail, { loading }] = useLazyQuery(FORGOT_PASSWORD, {
    context: { clientName: "auth-api" },
    fetchPolicy: "network-only",
    errorPolicy: "all",
    onCompleted: (data) => {
      if (!data.forgotPassword.success) {
        setResetEmailError("An error occurred. Please try again.");
      } else {
        setEmailConfirmation(email.value);
      }
    },
    onError: (error) => {
      if (error) {
        if (error.message === "unauthorized") {
          setResetEmailError("No account exists with that email address. Please try again.");
        } else {
          setResetEmailError("An error occurred. Please try again.");
        }
      }
    },
  });

  const verifyForm = () => {
    let verifiedForm = {};

    if (email.value.trim().length === 0) {
      verifiedForm = { ...email, error: "Email address is required" };
    } else if (!isValidEmail(email.value.trim())) {
      verifiedForm = { ...email, error: "Invalid email address" };
    } else {
      verifiedForm = { ...email, error: "" };
    }

    setEmail(verifiedForm);

    if (!verifiedForm.error) {
      sendPasswordResetEmail({ variables: { email: verifiedForm.value } });
    }
  };

  return (
    <div>
      <div className="text-white text-[30px] font-bold font-barlow mb-[20px]">Recover Account</div>
      <div className="mb-[36px] text-lavender-50 text-[14px] font-inter">
        Enter the email address associated with your account and we'll send you a link to reset your password.
      </div>
      {resetEmailError && <div className="text-[12px] text-accent-red-450 font-[500] mb-[16px]">{resetEmailError}</div>}
      <CustomInput
        className={INPUT_CLASS}
        style={email.error ? { border: "1px solid #ff4e50", borderRadius: "3px" } : {}}
        value={email.value}
        error={email.error}
        errorSmall
        onChange={(e) => setEmail({ value: e.target.value, error: "" })}
        placeholder="Your email"
        label="Email"
        type="email"
        tabIndex={1000}
        autoFocus
      />
      <ButtonSuccess
        className="btn-blue w-full h-[36px] !text-[13px] mt-[24px]"
        label="Continue"
        onClick={verifyForm}
        isLoading={loading}
        bForceSubmitOnEnter={true}
      />
      <Link
        to={forwardMode(AppRoutes.LOGIN, search)}
        className="flex mt-[24px] text-[12px] text-lavender-50 font-inter underline items-center"
      >
        <ChevronLeft className="w-[10px] h-[10px] text-white mr-[8px]" />
        <span>Back to Login</span>
      </Link>
    </div>
  );
}
